import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { Badge, Button, Card, EmptyState, FIELD_CLASS } from '@/components/ui';
import { formatDate, formatDateTime } from '@/lib/format';
import type { ApiException } from '@/lib/api/types';
import type {
  CorpActionAdjustChange,
  CorpActionAdjustRequest,
  CorpActionAdjustResult,
  CorpActionRightsRequest,
  CorpActionSuspect,
} from './types';

/**
 * 기업행위(분할 · 병합) 의심 — 관리자 › 모의투자 탭 (2026-10-11, V148 · GPT [SPLIT-R1])
 *
 * 매매 직전 현재가가 전일 종가와 견줘 튀거나(국내 ±30% 밖 · 미국은 분할 비율에 맞는 배율이 두 시세 연속),
 * 과거 일봉 종가가 10% 넘게 소급해 바뀌면 그 종목을 **모든 모의 계좌에서** 사고팔지 않는다.
 * 1:10 분할 다음 날 분할 전 손절선과 견줘 "손절" 로 적히는 가짜 −90% 를 막는 장치다.
 *
 * 🔴 사람이 확인해 푼다
 *   오탐   — 매매를 다시 연다. 다음 시세부터 평소대로 판정한다(지난 가격으로 소급 체결하지 않는다).
 *   확인   — 기업행위가 맞다. 정지는 그대로다.
 *   비율 조정(1.5단계 · V149) — "a주 → b주" 와 효력일을 넣고 미리보기 → 적용. 열린 보유의 수량 × f · 가격 칸 ÷ f 로
 *            고치고 정지를 푼다. 현금 · 들인 돈은 그대로라 총자산이 안 바뀐다. 반 주가 생기면 서버가 막는다.
 */

const QUERY_KEY = ['trading', 'corp-actions'];

const STATUS_LABEL: Record<CorpActionSuspect['status'], { text: string; tone: 'danger' | 'warning' | 'neutral' }> = {
  SUSPECT: { text: '정지 · 확인 대기', tone: 'danger' },
  CONFIRMED: { text: '기업행위 확인 · 정지 유지', tone: 'warning' },
  FALSE_ALARM: { text: '오탐 · 재개', tone: 'neutral' },
  ADJUSTED: { text: '장부 조정 · 재개', tone: 'neutral' },
};

/** 바뀌는 행의 표 이름 → 화면 이름 */
const TABLE_LABEL: Record<string, string> = {
  td_paper_trade: '추천 체결',
  td_recommendation: '추천 목표 · 손절',
  td_band_position: '저점매수 보유',
  td_band_shadow_ladder: '그림자 사다리',
  td_swing_position: '스윙 보유',
  td_swing_reentry: '스윙 재진입선',
  td_band_target: '저점매수 감시 목록',
  td_swing_target: '스윙 감시 목록',
  td_paper_account: '추천 계좌 예수금',
  td_band_account: '저점매수 계좌 예수금',
  td_swing_account: '스윙 계좌 예수금',
};

/** 칸 이름 → 화면 이름 (서버 CorpActionAdjustService 의 칸 이름과 같다) */
const FIELD_LABEL: Record<string, string> = {
  quantity: '수량',
  qtyAtFirstLeg: '첫 익절 때 수량',
  buyPrice: '매수가',
  stopOverridePrice: '본전선',
  entryPrice: '진입가',
  targetPrice: '목표가',
  targetPriceFirst: '1차 목표가',
  stopLossPrice: '손절가',
  avgPrice: '평단',
  stopPrice: '손절선',
  lastFill: '직전 체결가',
  atrAtEntry: '진입 ATR',
  refPrice: '기준가',
  lastMark: '직전 단 판정가',
  entryAvg: '고정 평단',
  stopMark: '재진입선',
  bandPrice: '매수선',
  midPrice: '중앙',
  atr14: 'ATR',
  atr: 'ATR',
  lowMin: '최저',
  highMax: '최고',
  cost: '들인 돈',
  cash: '예수금',
  rightsDeposit: '권리가치 입금',
};

function changeText(c: CorpActionAdjustChange): string {
  const fields = Object.keys(c.before)
    .filter((k) => c.before[k] !== null)
    .map((k) => {
      const before = c.before[k]?.toLocaleString();
      const after = c.after ? ` → ${c.after[k]?.toLocaleString()}` : '';
      return `${FIELD_LABEL[k] ?? k} ${before}${after}`;
    });
  return fields.join(' · ');
}

const SOURCE_LABEL: Record<CorpActionSuspect['source'], string> = {
  PRICE_JUMP: '현재가가 전일 종가와 견줘 튐',
  CANDLE_REVISION: '과거 일봉이 소급해 바뀜',
  STALE_BASE: '전일 종가가 낡았는데 현재가가 크게 다름(일봉 수집 확인)',
  BASE_RESET: '장 전 기준가가 전일 정규장 종가와 다름(거래소 기준가 재설정)',
  NO_LIMIT: '들고 있는 종목에 오늘 상 · 하한가가 없음(정리매매 · 재상장 등)',
};

export function CorpActionPanel() {
  const queryClient = useQueryClient();
  const list = useQuery<CorpActionSuspect[], ApiException>({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<CorpActionSuspect[]>('/admin/trading/corp-actions'),
  });

  const resolve = useMutation<void, ApiException, { id: number; action: 'dismiss' | 'confirm' }>({
    mutationFn: ({ id, action }) => api.post<void>(`/admin/trading/corp-actions/${id}/${action}`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const rows = list.data ?? [];
  const [symbol, setSymbol] = useState('');

  return (
    <Card title="기업행위(분할 · 병합) 의심">
      <p className="text-xs text-fg-muted">
        의심 종목은 모든 모의 계좌에서 손절 · 익절 · 만료 · 추가 매수 · 새 매수를 멈춥니다. 확인해서{' '}
        <strong>오탐</strong>이면 풀고, 분할 · 병합 · 무상증자 · 유상증자 권리락이 맞으면 아래 <strong>장부 조정</strong>으로 고쳐
        정지를 풉니다. 지난 30일 감지분입니다.
      </p>
      <p className="mt-1 text-xs text-fg-muted">
        평일 07:25 · 07:50 · 08:35 에 장 전 기준가 검사가 돕니다. 그날 상 · 하한가가 오늘 값인지 확인되지 않으면
        08:00 ~ 09:00 국내 장전 매매를 전부 멈추고, 09:00 부터는 평소대로 돌며 관리자에게 알립니다.
        분할 · 병합 · 증자 같은 공시는 평일 07:40 · 18:40 에 관리자에게 미리 알립니다.
        검사 · 알림 대상은 모의 계좌가 들고 있거나 곧 살 수 있는 국내 종목과 내 실계좌(토스) 보유 국내 종목입니다. 의심이 잡히면 모의 계좌는 위처럼 멈추고, 실계좌는 우리 시스템이 매매하지 않으니 알림을 보고 토스 앱에서 확인합니다.
      </p>

      {list.isLoading && <p className="mt-3 text-sm text-fg-muted">불러오는 중입니다.</p>}
      {list.error && <p className="mt-3 text-sm text-danger">{list.error.message}</p>}
      {!list.isLoading && !list.error && rows.length === 0 && (
        <EmptyState title="지난 30일 감지된 의심이 없습니다" />
      )}

      {rows.length > 0 && (
        <ul className="mt-3 space-y-2">
          {rows.map((r) => {
            const status = STATUS_LABEL[r.status];
            return (
              <li key={r.id} className="rounded-lg border border-line p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {r.name ?? '알 수 없는 종목'} {r.symbol ? `(${r.symbol})` : ''}
                  </span>
                  <Badge tone={status.tone}>{status.text}</Badge>
                </div>
                <p className="mt-1 text-xs text-fg-muted">
                  {formatDate(r.detectedOn)} · {SOURCE_LABEL[r.source]}
                  {r.candleDate ? `(${formatDate(r.candleDate)} 봉)` : ''} · {r.refPrice.toLocaleString()} →{' '}
                  {r.observedPrice.toLocaleString()} (×{r.ratio}
                  {r.factorGuess ? ` · 맞는 비율 ${r.factorGuess}` : ''})
                </p>
                {r.resolvedAt && (
                  <p className="mt-0.5 text-xs text-fg-muted">처리 {formatDateTime(r.resolvedAt)}</p>
                )}
                {r.status === 'SUSPECT' && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      disabled={resolve.isPending}
                      onClick={() => resolve.mutate({ id: r.id, action: 'dismiss' })}
                    >
                      오탐 — 매매 재개
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={resolve.isPending}
                      onClick={() => resolve.mutate({ id: r.id, action: 'confirm' })}
                    >
                      기업행위 맞음 — 정지 유지
                    </Button>
                  </div>
                )}
                {(r.status === 'SUSPECT' || r.status === 'CONFIRMED') && r.symbol && (
                  <div className="mt-2">
                    <Button variant="ghost" onClick={() => setSymbol(r.symbol ?? '')}>
                      이 종목으로 장부 조정
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {resolve.error && <p className="mt-2 text-sm text-danger">{resolve.error.message}</p>}

      <AdjustForm symbol={symbol} onSymbolChange={setSymbol} />
    </Card>
  );
}

/**
 * 장부 조정 — 미리보기 → 적용 (1.5단계 · V149)
 *
 * 두 종류다.
 *   분할 · 병합 · 무상증자 — "a주 → b주" · 수량 × f · 가격 ÷ f
 *   유상증자 권리락 — 전일 종가 · 권리락 기준가 · 수량 그대로 · 가격선 × k · 권리가치를 계좌 예수금에(GPT [SPLIT-R3] Q5)
 * 🔴 적용은 미리보기를 본 입력 그대로일 때만 누를 수 있다 — 입력을 바꾸면 미리보기가 지워진다(본 것과 다른 것을 적용하지 않게).
 */
function AdjustForm({ symbol, onSymbolChange }: { symbol: string; onSymbolChange: (s: string) => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'SPLIT' | 'RIGHTS'>('SPLIT');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [from, setFrom] = useState('1');
  const [to, setTo] = useState('');
  const [prevClose, setPrevClose] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [preview, setPreview] = useState<CorpActionAdjustResult | null>(null);

  const request: CorpActionAdjustRequest = {
    symbol: symbol.trim(),
    effectiveDate,
    from: Number(from),
    to: Number(to),
  };
  const rightsRequest: CorpActionRightsRequest = {
    symbol: symbol.trim(),
    effectiveDate,
    prevClose: Number(prevClose),
    basePrice: Number(basePrice),
  };
  const filled =
    request.symbol !== '' &&
    effectiveDate !== '' &&
    (kind === 'SPLIT'
      ? Number.isInteger(request.from) &&
        Number.isInteger(request.to) &&
        request.from > 0 &&
        request.to > 0 &&
        request.from !== request.to
      : rightsRequest.prevClose > 0 && rightsRequest.basePrice > 0 && rightsRequest.basePrice < rightsRequest.prevClose);

  const path = kind === 'SPLIT' ? 'adjust' : 'rights';
  const body = kind === 'SPLIT' ? request : rightsRequest;

  const runPreview = useMutation<CorpActionAdjustResult, ApiException, void>({
    mutationFn: () => api.post<CorpActionAdjustResult>(`/admin/trading/corp-actions/${path}/preview`, body),
    onSuccess: setPreview,
  });
  const runApply = useMutation<CorpActionAdjustResult, ApiException, void>({
    mutationFn: () => api.post<CorpActionAdjustResult>(`/admin/trading/corp-actions/${path}/apply`, body),
    onSuccess: (result) => {
      setPreview(result);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  // 입력이 바뀌면 본 미리보기를 지운다
  const edit = (apply: () => void) => {
    apply();
    setPreview(null);
    runApply.reset();
  };

  // 위 목록의 "이 종목으로 장부 조정" 은 종목 칸을 밖에서 바꾼다 — 그래서 지우기와 별도로, 본 미리보기가 지금 입력과 같은지도 본다
  const previewMatches =
    preview !== null &&
    preview.kind === kind &&
    preview.symbol === request.symbol &&
    preview.effectiveDate === effectiveDate &&
    (kind === 'SPLIT'
      ? preview.from === request.from && preview.to === request.to
      : preview.prevClose === rightsRequest.prevClose && preview.basePrice === rightsRequest.basePrice);
  const canApply = previewMatches && !preview.applied && preview.blockers.length === 0 && !runApply.isPending;

  return (
    <div className="mt-4 border-t border-line pt-3">
      <h3 className="text-sm font-medium">장부 조정</h3>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button variant={kind === 'SPLIT' ? 'primary' : 'secondary'} onClick={() => edit(() => setKind('SPLIT'))}>
          분할 · 병합 · 무상증자
        </Button>
        <Button variant={kind === 'RIGHTS' ? 'primary' : 'secondary'} onClick={() => edit(() => setKind('RIGHTS'))}>
          유상증자 권리락
        </Button>
      </div>
      <p className="mt-1 text-xs text-fg-muted">
        {kind === 'SPLIT'
          ? '효력일 전에 산 열린 보유의 수량을 비율만큼 늘리고(줄이고), 매수가 · 손절선 · 목표가 같은 가격 칸을 같은 비율로 나눕니다. ' +
            '현금과 들인 돈은 그대로라 총자산은 바뀌지 않습니다. 반 주가 생기는 비율(예: 4주 → 5주)은 막힙니다.'
          : '수량은 그대로 두고, 손절선 · 목표가 같은 가격선을 (기준가 ÷ 전일 종가) 비율로 낮춥니다. 주당 권리가치(전일 종가 − 기준가) × ' +
            '보유 주식 수만큼 들인 돈을 줄이고 그 돈을 계좌 예수금에 넣어, 권리락으로 떨어진 만큼이 가짜 손실이 되지 않게 합니다. 국내 종목만 됩니다.'}{' '}
        효력일 장중에 적용해야 그날 저녁 일일 평가에 가짜 급락이 찍히지 않습니다.
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label className="block">
          <span className="text-xs text-fg-muted">종목 코드</span>
          <input
            className={FIELD_CLASS}
            value={symbol}
            onChange={(e) => edit(() => onSymbolChange(e.target.value))}
            placeholder="005930 · NVDA"
          />
        </label>
        <label className="block">
          <span className="text-xs text-fg-muted">효력일(미국은 현지 날짜)</span>
          <input
            type="date"
            className={FIELD_CLASS}
            value={effectiveDate}
            onChange={(e) => edit(() => setEffectiveDate(e.target.value))}
          />
        </label>
        {kind === 'SPLIT' ? (
          <>
            <label className="block">
              <span className="text-xs text-fg-muted">옛 주식 수</span>
              <input
                className={FIELD_CLASS}
                inputMode="numeric"
                value={from}
                onChange={(e) => edit(() => setFrom(e.target.value))}
              />
            </label>
            <label className="block">
              <span className="text-xs text-fg-muted">새 주식 수</span>
              <input
                className={FIELD_CLASS}
                inputMode="numeric"
                value={to}
                onChange={(e) => edit(() => setTo(e.target.value))}
                placeholder="1주 → 5주 분할이면 5"
              />
            </label>
          </>
        ) : (
          <>
            <label className="block">
              <span className="text-xs text-fg-muted">전일 종가(KRX)</span>
              <input
                className={FIELD_CLASS}
                inputMode="decimal"
                value={prevClose}
                onChange={(e) => edit(() => setPrevClose(e.target.value))}
              />
            </label>
            <label className="block">
              <span className="text-xs text-fg-muted">권리락 기준가(KRX 공시)</span>
              <input
                className={FIELD_CLASS}
                inputMode="decimal"
                value={basePrice}
                onChange={(e) => edit(() => setBasePrice(e.target.value))}
              />
            </label>
          </>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button variant="secondary" disabled={!filled || runPreview.isPending} onClick={() => runPreview.mutate()}>
          {runPreview.isPending ? '보는 중' : '미리보기'}
        </Button>
        <Button variant="danger" disabled={!canApply} onClick={() => runApply.mutate()}>
          {runApply.isPending ? '적용하는 중' : '적용 — 장부 고치고 매매 재개'}
        </Button>
      </div>
      {runPreview.error && <p className="mt-2 text-sm text-danger">{runPreview.error.message}</p>}
      {runApply.error && <p className="mt-2 text-sm text-danger">{runApply.error.message}</p>}

      {preview && (
        <div className="mt-3 text-sm">
          <p className="font-medium">
            {preview.name} ({preview.symbol}) · {formatDate(preview.effectiveDate)} ·{' '}
            {preview.kind === 'RIGHTS'
              ? `권리락 ${preview.prevClose?.toLocaleString()} → ${preview.basePrice?.toLocaleString()}`
              : `${preview.from}주 → ${preview.to}주`}
            {preview.applied ? ' — 적용했습니다' : ' — 미리보기'}
          </p>
          {preview.blockers.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-danger">
              {preview.blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
          {preview.changes.length === 0 ? (
            <p className="mt-1 text-xs text-fg-muted">
              고칠 열린 보유 · 감시 행이 없습니다. 적용하면 기록 한 줄만 남기고 정지를 풉니다.
            </p>
          ) : (
            <ul className="mt-1 space-y-1">
              {preview.changes.map((c) => (
                <li key={`${c.table}-${c.id}`} className="break-keep text-xs">
                  <span className="font-medium">
                    {TABLE_LABEL[c.table] ?? c.table} #{c.id}
                    {c.accountId !== null ? ` (계좌 ${c.accountId})` : ''}
                  </span>{' '}
                  — {changeText(c)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
